import { useCanImportFiles } from "@/features/authentication";
import DialogBase from "@components/ui/dialog-base";
import { useEffect, useRef } from "react";
import { useFileToStorydocFlow } from "../hooks/use-file-to-storydoc-flow";
import { useDrivePick } from "../hooks/use-drive-pick";
import {
  type FileToStorydocTarget,
  useFileToStorydocStore,
  useFileToStorydocStoreShallow,
} from "../stores";
import {
  selectAllFinal,
  selectIsImportPending,
  type ImportEntryPoint,
} from "../stores/file-to-storydoc-generation.store";
import {
  countActiveImports,
  getViewingImport,
  isUploadOwnedByImport,
  selectAtCap,
  selectViewing,
  useImportRegistry,
} from "../stores/import-registry.store";
import { ImportStoreProvider } from "../stores/import-store-context";
import { IMPORT_DIALOG_ID, closeImportDialog } from "../utils/import-dialog-id";
import { trackImport, trackImportPreJob } from "../utils/track-import";
import { ErrorStep } from "./error-step";
import { ImportDialogHeader } from "./import-dialog-header";
import { SelectPagesStep } from "./select-pages-step";
import { SourceStep } from "./source-step";
import { UploadingStep } from "./uploading-step";
import { VersionsStep } from "./versions-step";

type Props = {
  file?: File;
  initialStep?: "source" | "choosing";
  initialSource?: "drive";
  entryPoint?: ImportEntryPoint;
  target?: FileToStorydocTarget;
  importId?: string;
  /**
   * Called when the dialog closes WITHOUT handing off to generation (the
   * close button, Escape, an unrecoverable error the user walks away from).
   * The agent uses it to close off the chat turn it opened the dialog for.
   */
  onDismissed?: () => void;
};

const revokeThumbnailsOf = (pages: { thumbnailUrl: string }[]) => {
  for (const p of pages)
    if (p.thumbnailUrl.startsWith("blob:")) URL.revokeObjectURL(p.thumbnailUrl);
};

export const FileToStorydocDialog = ({
  file,
  initialStep = "source",
  initialSource,
  entryPoint = "unknown",
  target = "new-story",
  importId,
  onDismissed,
}: Props) => {
  const { step, originalFile, pdfUrl, source } = useFileToStorydocStoreShallow(
    (s) => ({
      step: s.step,
      originalFile: s.originalFile,
      pdfUrl: s.pdfUrl,
      source: s.source,
    }),
  );
  const viewingEntry = useImportRegistry(selectViewing);
  const jobFileName = viewingEntry?.store.getState().fileName ?? null;
  const viewingHasUpload = viewingEntry?.store.getState().upload != null;
  const {
    startUpload,
    startDriveUpload,
    continueToGeneration,
    backToSource,
    backToPages,
    abortUpload,
  } = useFileToStorydocFlow();
  const { pickFromDrive, pending: drivePending } =
    useDrivePick(startDriveUpload);
  const isDeck = target === "deck";
  // the editor agent's deck import was never role-gated; only the dashboard import is
  const canImport = useCanImportFiles() || isDeck;
  // Read through a ref inside the mount-only effect so its cleanup sees the
  // callback this dialog was opened with, not a stale first render.
  const onDismissedRef = useRef(onDismissed);
  onDismissedRef.current = onDismissed;

  useEffect(() => {
    if (!canImport) closeImportDialog();
  }, [canImport]);

  // Mount-only: the dialog opens with fixed props and must not re-run on re-render.
  useEffect(() => {
    if (!canImport) return;
    const local = useFileToStorydocStore.getState();
    const registry = useImportRegistry.getState();
    const atCap = !isDeck && selectAtCap(registry);
    const opened = importId
      ? registry.imports.find((i) => i.id === importId)
      : undefined;
    // the agent sets theme-only right before opening; reset() must not drop it
    const { themeOnly } = local;
    local.reset();
    local.setTarget(target);
    if (isDeck) local.setThemeOnly(themeOnly);
    if (
      initialStep === "choosing" &&
      opened &&
      selectIsImportPending(opened.store.getState())
    ) {
      registry.setViewingId(opened.id);
      local.setEntryPoint(opened.store.getState().entryPoint);
      local.setStep("choosing");
      opened.store.getState().setModalOpen(true);
    } else {
      local.setEntryPoint(entryPoint);
      if (!atCap) {
        if (file) startUpload(file);
        else if (initialSource === "drive") void pickFromDrive();
      }
    }
    const openedStep = useFileToStorydocStore.getState().step;
    if (openedStep === "choosing") {
      trackImport("modal opened", { step: openedStep }, opened?.store ?? null);
    } else {
      trackImportPreJob("modal opened", {
        step: openedStep,
        entryPoint,
        ...(initialSource ? { source: initialSource } : {}),
      });
      if (atCap)
        trackImportPreJob("cap reached", {
          imports: countActiveImports(registry.imports),
        });
    }
    return () => {
      abortUpload();
      const { continued, pdfUrl, pages } = useFileToStorydocStore.getState();
      if (!continued) onDismissedRef.current?.();
      getViewingImport()?.store.getState().setModalOpen(false);
      useImportRegistry.getState().setViewingId(null);
      if (!isUploadOwnedByImport(pdfUrl)) revokeThumbnailsOf(pages);
      useFileToStorydocStore.getState().reset();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleClose = () => {
    trackImport("closed", {
      step,
      allFinal: selectAllFinal(getViewingImport()?.store.getState().rows ?? []),
    });
    closeImportDialog();
  };

  const fileName =
    originalFile?.name ?? (step === "choosing" ? jobFileName : null);
  const onBack =
    step === "selecting" || step === "uploading" || step === "error"
      ? backToSource
      : step === "choosing" && (pdfUrl || viewingHasUpload)
        ? backToPages
        : undefined;

  if (!canImport) return null;

  return (
    <DialogBase className="tw-relative tw-flex tw-h-[min(841px,calc(100vh-64px))] tw-w-[min(1248px,calc(100vw-48px))] tw-flex-col tw-gap-10 tw-overflow-hidden tw-rounded-3xl tw-px-6 tw-pb-6 tw-pt-14 tw-shadow-sm">
      <ImportDialogHeader
        step={step}
        isDeck={isDeck}
        fileName={step === "source" ? null : fileName}
        onBack={onBack}
        onClose={handleClose}
      />
      {step === "source" ? (
        <SourceStep
          onPickLocal={(f) => startUpload(f, "local")}
          onPickDrive={pickFromDrive}
          drivePending={drivePending}
        />
      ) : step === "uploading" ? (
        <UploadingStep />
      ) : step === "selecting" ? (
        <SelectPagesStep onContinue={() => void continueToGeneration()} />
      ) : step === "choosing" ? (
        viewingEntry ? (
          <ImportStoreProvider store={viewingEntry.store}>
            <VersionsStep />
          </ImportStoreProvider>
        ) : null
      ) : (
        <ErrorStep
          onRetry={() => originalFile && startUpload(originalFile, source)}
          onClose={handleClose}
        />
      )}
    </DialogBase>
  );
};

FileToStorydocDialog.dialogId = IMPORT_DIALOG_ID;
